import { useAsyncList } from '@react-stately/data';
import React, { Key, useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { format } from 'date-fns';
import isFileImage from 'src/features/product/product-detail/utils/isFileImage';
import { AppImage } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { Sampling } from 'src/shared/types/sampling';
import isValidDate from 'src/shared/utils/isValidDate';

import ExpectedTag from 'src/shared/components/ExpectedTag';
import {
  SortDescriptor,
  Spinner,
  useDisclosure
} from 'src/shared/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography
} from 'src/shared/components/ui';

import samplingsTableColumns from '../../constants/samplingsTabeColumns';
import getCompletedDate from '../../utils/getCompletedDate';
import SampleStage from '../sample-stage/SampleStage';
import SamplingModal from '../sampling-modal/SamplingModal';

const getSamplingsByFilter = (
  samplings: Sampling[],
  filterText: string | undefined
) => {
  // First filter out archived samples
  const nonArchivedSamplings = samplings?.filter(
    (sampling: Sampling) => !sampling.archived
  );

  // Then apply supplier filter if needed
  if (!filterText) return nonArchivedSamplings;
  if (filterText === 'all') return nonArchivedSamplings;

  return nonArchivedSamplings?.filter((sampling: Sampling) => {
    return sampling?.cloak === filterText;
  });
};

interface SamplingsTableProps {
  samplings: Sampling[];
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  selectedSupplier: string;
}

const SamplingsTable: React.FC<SamplingsTableProps> = ({
  samplings,
  isLoading,
  setIsLoading,
  selectedSupplier
}) => {
  const { id } = useParams<{ id: string }>();
  const { isOpen, onOpenChange, onOpen } = useDisclosure();
  const [selectedSampling, setSelectedSampling] = useState<Sampling | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const sampleId = searchParams.get('sample');

  useEffect(() => {
    if (samplings.length > 0 && sampleId) {
      const sampling = samplings.find((sampling) => sampling.id === sampleId);

      if (sampling) {
        setSelectedSampling(sampling);
        if (!isOpen) {
          onOpen();
        }
      }
    } else if (!sampleId && isOpen) {
      onOpenChange();
    }
  }, [samplings, sampleId, isOpen, onOpen, onOpenChange]);

  const handleModalClose = (newIsOpen: boolean) => {
    if (!newIsOpen && sampleId) {
      const newSearchParams = new URLSearchParams(searchParams);

      newSearchParams.delete('sample');
      setSearchParams(newSearchParams, { replace: true });
    }
    if (newIsOpen) {
      onOpen();
    } else {
      onOpenChange();
    }
  };

  const list = useAsyncList({
    async load() {
      setIsLoading(false);
      const filteredSamplings = getSamplingsByFilter(
        samplings,
        selectedSupplier
      );

      return {
        items: filteredSamplings
      };
    },
    async sort({ items, sortDescriptor, filterText }) {
      if (!sortDescriptor.direction) {
        const filteredSamplings = getSamplingsByFilter(samplings, filterText);

        return {
          items: [...filteredSamplings]
        };
      }

      return {
        items: items.sort((a: any, b: any) => {
          let first = a[sortDescriptor.column];
          let second = b[sortDescriptor.column];

          if (first == null) return 1;
          if (second == null) return -1;

          let cmp: number;

          if (
            typeof first === 'string' &&
            typeof second === 'string' &&
            isValidDate(first) &&
            isValidDate(second)
          ) {
            cmp = new Date(first).getTime() - new Date(second).getTime();
          } else {
            const firstNum = Number(first);
            const secondNum = Number(second);

            if (!isNaN(firstNum) && !isNaN(secondNum)) {
              cmp = (firstNum || 0) - (secondNum || 0);
            } else {
              cmp = String(first).localeCompare(String(second), undefined, {
                sensitivity: 'base'
              });
            }
          }

          if (sortDescriptor.direction === 'descending') {
            cmp *= -1;
          }

          return cmp;
        })
      };
    }
  });

  useEffect(() => {
    list.setFilterText(selectedSupplier);
  }, [selectedSupplier, samplings]);

  const handleSortChange = (descriptor: SortDescriptor) => {
    if (
      list.sortDescriptor?.column === descriptor.column &&
      list.sortDescriptor?.direction === 'descending'
    ) {
      list.sort({ column: undefined, direction: undefined } as any);
    } else {
      list.sort(descriptor);
    }
  };

  const renderCell = useCallback((item: any, columnKey: Key) => {
    switch (columnKey) {
      case 'images':
        const images = item?.uploaded_files?.filter(isFileImage);

        return (
          <div className="flex items-center gap-2">
            <AppImage
              id={item?.id}
              className="size-16 object-cover min-w-16 rounded-xl bg-gray-2 border border-gray-4"
              src={images?.[0]?.url}
            />
            <AppImage
              id={item?.id}
              className="size-16 object-cover min-w-16 rounded-xl bg-gray-2 border border-gray-4"
              src={images?.[1]?.url}
            />
          </div>
        );

      case 'name':
        return (
          <div className="flex items-center gap-2">
            <Typography variant="body-2" weight="medium">
              {item.staffOnly && (
                <span
                  className="text-p-cavela-blue"
                  title="Only impersonating staff can see this"
                >
                  🕵🏻
                </span>
              )}
              {item.name ?? '-'}
            </Typography>
          </div>
        );

      case 'type':
        return (
          <Typography variant="body-2" className="truncate">
            {item?.product_quote?.product_name}
          </Typography>
        );

      case 'supplier_name':
        return <Typography variant="body-2">{item?.cloak}</Typography>;

      case 'stage':
        return <SampleStage stage={item.stage} variant="body-2" />;

      case 'requested_at':
        return (
          <Typography variant="body-2">
            {format(item.requested_at, 'MMM dd, yyyy')}
          </Typography>
        );

      case 'completion_at': {
        const completedDate = getCompletedDate(item);

        return (
          <div className="flex items-center gap-2">
            <Typography variant="body-2">{completedDate.date}</Typography>
            {completedDate.is_expected && <ExpectedTag />}
          </div>
        );
      }

      default:
        return '';
    }
  }, []);

  return (
    <div className="flex flex-col">
      <Table
        aria-label="Samplings table"
        sortDescriptor={list?.sortDescriptor}
        onSortChange={handleSortChange}
      >
        <TableHeader columns={samplingsTableColumns}>
          {(column) => (
            <TableColumn
              allowsSorting={column.allowsSorting}
              className="px-4"
              key={column.key}
            >
              {column.label}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          emptyContent={
            isLoading ? (
              <Spinner size="lg" />
            ) : (
              list?.items?.length === 0 && 'No samples'
            )
          }
          items={list?.items ?? []}
          isLoading={isLoading}
        >
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => (
                <TableCell>
                  <Link
                    className="!h-24 flex items-center"
                    to={`${ROUTE_PATHS.PRODUCTS}/${id}?tab=samples&sample=${item.id}`}
                  >
                    <div className="truncate">
                      {renderCell(item, columnKey)}
                    </div>
                  </Link>
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <SamplingModal
        setSelectedSampling={setSelectedSampling}
        isOpen={isOpen}
        onOpenChange={handleModalClose}
        sampling={selectedSampling}
      />
    </div>
  );
};

export default SamplingsTable;
